import {
  createContext,
  useContext,
  useState,
  type PropsWithChildren,
} from 'react';
import { createPortal } from 'react-dom';
import { SidebarMenuItem } from '@/components/ui/sidebar';

/**
 * Shared sidebar "Help" section.
 *
 * Owns the visible group inside the sidebar footer and provides a portal slot
 * so trigger components (feedback, AI assistant, …) can register their own
 * sidebar menu item from anywhere in the tree, without depending on each
 * other's mount points.
 *
 * Each slot specifies a stable `order` so the visual ordering is deterministic
 * regardless of which component mounts first. Lower numbers render first.
 */
interface HelpMenuContextValue {
  slotEl: HTMLUListElement | null;
  setSlotEl: (el: HTMLUListElement | null) => void;
}

const HelpMenuContext = createContext<HelpMenuContextValue>({
  slotEl: null,
  setSlotEl: () => {},
});

export const HELP_MENU_ORDER = {
  documentation: 50,
  search: 100,
  feedback: 200,
  ai: 300,
} as const;

export function HelpMenuProvider({ children }: PropsWithChildren) {
  const [slotEl, setSlotEl] = useState<HTMLUListElement | null>(null);
  return (
    <HelpMenuContext.Provider value={{ slotEl, setSlotEl }}>
      {children}
    </HelpMenuContext.Provider>
  );
}

export function useHelpMenuSlotTarget() {
  return useContext(HelpMenuContext).setSlotEl;
}

interface HelpMenuSlotProps extends PropsWithChildren {
  /**
   * Stable layout order — lower numbers render closer to the top of the menu.
   * Use values from `HELP_MENU_ORDER` so all slots share a single source of
   * truth.
   */
  order: number;
}

/**
 * Portal a trigger into the shared Help menu in the sidebar footer. Returns
 * null if the section hasn't mounted yet (e.g. before HelpMenuSection's ref
 * callback fires on first render).
 *
 * `children` should be the content of a single menu item (typically a
 * `<SidebarMenuButton>` with icon + label) — this component wraps it in a
 * `<SidebarMenuItem>` so the consumer doesn't have to.
 */
export function HelpMenuSlot({ order, children }: HelpMenuSlotProps) {
  const { slotEl } = useContext(HelpMenuContext);
  if (!slotEl) return null;
  return createPortal(
    <SidebarMenuItem style={{ order }}>{children}</SidebarMenuItem>,
    slotEl,
  );
}
